import { createContext, useContext, useEffect, useState } from "react";
import { api } from "../lib/api";

export const CURRENCIES = [
  { code: "INR", name: "Indian Rupee", locale: "en-IN", decimals: 0 },
  { code: "USD", name: "US Dollar", locale: "en-US", decimals: 2 },
  { code: "AED", name: "UAE Dirham", locale: "en-AE", decimals: 2 },
  { code: "AUD", name: "Australian Dollar", locale: "en-AU", decimals: 2 },
  { code: "CAD", name: "Canadian Dollar", locale: "en-CA", decimals: 2 },
  { code: "GBP", name: "British Pound", locale: "en-GB", decimals: 2 },
  { code: "EUR", name: "Euro", locale: "de-DE", decimals: 2 },
  { code: "CHF", name: "Swiss Franc", locale: "de-CH", decimals: 2 },
  { code: "QAR", name: "Qatari Riyal", locale: "en-QA", decimals: 2 },
  { code: "MYR", name: "Malaysian Ringgit", locale: "en-MY", decimals: 2 },
];

export const currencyMeta = (code) => CURRENCIES.find((c) => c.code === code) || CURRENCIES[0];

const CurrencyContext = createContext(null);
export const useCurrency = () => useContext(CurrencyContext);

export function CurrencyProvider({ children }) {
  const [currency, setCurrencyState] = useState(() => {
    const stored = localStorage.getItem("b2l_currency");
    return CURRENCIES.some((c) => c.code === stored) ? stored : "INR";
  });
  const [fx, setFx] = useState({ rates: { INR: 1 }, updated_at: null, source: "loading" });

  useEffect(() => {
    api
      .get("/fx/rates")
      .then((res) => setFx({ rates: res.data.rates, updated_at: res.data.updated_at, source: res.data.source }))
      .catch(() => setFx({ rates: { INR: 1 }, updated_at: null, source: "unavailable" }));
  }, []);

  const setCurrency = (code) => {
    localStorage.setItem("b2l_currency", code);
    setCurrencyState(code);
  };

  const meta = currencyMeta(currency);

  const format = (amount, code = currency) => {
    const m = currencyMeta(code);
    try {
      return new Intl.NumberFormat(m.locale, {
        style: "currency",
        currency: code,
        maximumFractionDigits: m.decimals,
      }).format(amount ?? 0);
    } catch {
      return `${code} ${Number(amount || 0).toLocaleString()}`;
    }
  };

  // Base pricing is always INR. Converts to the selected display currency using live/cached FX.
  const convert = (inrAmount, to = currency) => {
    if (to === "INR") return inrAmount;
    const rate = fx.rates?.[to];
    if (!rate) return null;
    return inrAmount * rate;
  };

  // Display helper: base INR amount -> formatted selected currency (falls back to INR if FX unavailable)
  const money = (inrAmount) => {
    if (currency === "INR") return format(inrAmount, "INR");
    const converted = convert(inrAmount);
    if (converted === null) return format(inrAmount, "INR");
    return format(converted, currency);
  };

  return (
    <CurrencyContext.Provider
      value={{
        currency,
        setCurrency,
        currencies: CURRENCIES,
        fx,
        format,
        convert,
        money,
        meta,
        fxUnavailable: currency !== "INR" && !fx.rates?.[currency],
      }}
    >
      {children}
    </CurrencyContext.Provider>
  );
}
